import React, { useState, useEffect, useRef } from 'react';
import { adminApi } from '../api';
import type { AdminFlowModel, AdminFlowQuestion } from '../types';
import QuestionEditModal from './QuestionEditModal';
import QuestionPreviewCard from './QuestionPreviewCard';
import AddQuestionBar, { PRESET_QUESTIONS } from './AddQuestionBar';
import { INPUT } from '../../styles/tokens';

function compressPages(qs: AdminFlowQuestion[]): AdminFlowQuestion[] {
  const nonEmptyPages = [...new Set(qs.map(q => q.page))].sort((a, b) => a - b);
  const pageMap: Record<number, number> = Object.fromEntries(nonEmptyPages.map((p, i) => [p, i + 1]));
  return qs.map(q => ({ ...q, page: pageMap[q.page] }));
}

function normalizeQuestionBeforeSave(q: AdminFlowQuestion): AdminFlowQuestion {
  const normalized = { ...q };
  if (q.type === 'image') {
    normalized.minUpload = q.minUpload ?? (q.required ? 1 : 0);
    normalized.maxUpload = q.maxUpload ?? 1;
  } else {
    delete normalized.minUpload;
    delete normalized.maxUpload;
  }
  if (q.type === 'select' || q.type === 'text' || q.type === 'image' || q.type === 'boolean') {
    normalized.options = q.options ?? [];
  }
  if (q.type === 'text') {
    normalized.templates = q.templates ?? [];
  } else {
    delete normalized.templates;
  }
  return normalized;
}

function emptyQuestion(type: AdminFlowQuestion['type'], page: number): AdminFlowQuestion {
  return {
    id: crypto.randomUUID(), type, title: '', page, required: true,
    prompt: type === 'boolean' ? { true: '', false: '' } : '',
    options: [],
  };
}

interface FlowModelEditorProps {
  mode: 'create' | 'edit';
  initial: AdminFlowModel;
  onDone: () => void;
  onSectionDataChange?: (data: Record<string, any>) => void;
}

const INP = `text-sm rounded px-2 py-1.5 ${INPUT.base}`;

const FlowModelEditor: React.FC<FlowModelEditorProps> = ({ mode, initial, onDone, onSectionDataChange }) => {
  const [form, setForm] = useState<AdminFlowModel>({ ...initial, questions: [...initial.questions] });
  const [activePage, setActivePage] = useState(1);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [supplementTools, setSupplementTools] = useState<{ id: string; label: string }[]>([]);
  const [imageMode, setImageMode] = useState<'url' | 'upload'>(initial.image ? 'url' : 'url');
  const [imageUploading, setImageUploading] = useState(false);
  const [dragQFrom, setDragQFrom] = useState<number | null>(null);
  const [dragQOver, setDragQOver] = useState<number | null>(null);

  const isDirty = JSON.stringify(form) !== JSON.stringify(initial);

  useEffect(() => {
    adminApi.getToolkitCategories().then(cats => setCategories(cats.map(c => c.name))).catch(() => {});
    adminApi.getSupplementTools().then(setSupplementTools).catch(() => {});
  }, []);

  useEffect(() => {
    onSectionDataChange?.({ view: 'editor', mode, currentModel: form, activePage });
  }, [form, mode, activePage, onSectionDataChange]);

  const formRef = useRef(form);
  useEffect(() => { formRef.current = form; }, [form]);
  const activePageRef = useRef(activePage);
  useEffect(() => { activePageRef.current = activePage; }, [activePage]);

  // 監聽 AI 小幫手事件
  useEffect(() => {
    const handleSetMeta = (e: Event) => {
      const payload = (e as CustomEvent<Record<string, string>>).detail;
      setForm(f => ({ ...f, ...payload }));
      setShowAdvanced(prev => prev || 'hiddenImagePrompt' in payload);
    };

    const handleAddPreset = (e: Event) => {
      const { presetId } = (e as CustomEvent<{ presetId: string }>).detail;
      const preset = PRESET_QUESTIONS.find(p => p.id === presetId);
      if (preset && !formRef.current.questions.find((q: AdminFlowQuestion) => q.id === presetId)) {
        setForm(f => ({ ...f, questions: [...f.questions, { ...preset, page: activePageRef.current }] }));
      }
    };

    type QuestionDetail = {
      id: string;
      type: AdminFlowQuestion['type'];
      title: string;
      required: boolean;
      promptStr?: string;
      promptTrue?: string;
      promptFalse?: string;
      options?: { value: string; label: string; prompt?: string }[];
    };

    const handleAddQuestion = (e: Event) => {
      const d = (e as CustomEvent<QuestionDetail>).detail;
      if (!d?.id || !d.type || !d.title) return;
      const prompt: AdminFlowQuestion['prompt'] =
        d.type === 'boolean'
          ? { true: d.promptTrue ?? '', false: d.promptFalse ?? '' }
          : (d.promptStr ?? '');
      const newQ: AdminFlowQuestion = {
        id: d.id,
        type: d.type,
        title: d.title,
        page: activePageRef.current,
        required: d.required ?? true,
        prompt,
        options: d.options ?? [],
      };
      setForm(f => ({ ...f, questions: [...f.questions, newQ] }));
    };

    const handleAddPage = () => {
      const currentQuestions = formRef.current.questions;
      const maxPage = currentQuestions.length > 0
        ? Math.max(...currentQuestions.map(q => q.page))
        : 1;
      setActivePage(maxPage + 1);
    };

    const handleSetPage = (e: Event) => {
      const { page } = (e as CustomEvent<{ page: number }>).detail;
      if (typeof page === 'number' && page >= 1) setActivePage(page);
    };

    window.addEventListener('admin-assistant:flow-model:set-meta', handleSetMeta);
    window.addEventListener('admin-assistant:flow-model:add-preset', handleAddPreset);
    window.addEventListener('admin-assistant:flow-model:add-question', handleAddQuestion);
    window.addEventListener('admin-assistant:flow-model:add-page', handleAddPage);
    window.addEventListener('admin-assistant:flow-model:set-page', handleSetPage);
    return () => {
      window.removeEventListener('admin-assistant:flow-model:set-meta', handleSetMeta);
      window.removeEventListener('admin-assistant:flow-model:add-preset', handleAddPreset);
      window.removeEventListener('admin-assistant:flow-model:add-question', handleAddQuestion);
      window.removeEventListener('admin-assistant:flow-model:add-page', handleAddPage);
      window.removeEventListener('admin-assistant:flow-model:set-page', handleSetPage);
    };
  }, []);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const setMeta = (key: keyof AdminFlowModel, value: string) =>
    setForm(f => ({ ...f, [key]: value }));

  const pageSet = new Set(form.questions.map(q => q.page));
  const pages = pageSet.size ? [...pageSet].sort((a, b) => a - b) : [1];
  const maxPage = Math.max(...pages);

  const pageQuestions = form.questions
    .map((q, i) => ({ q, i }))
    .filter(({ q }) => q.page === activePage);

  const existingIds = new Set(form.questions.map(q => q.id));

  const addPreset = (preset: AdminFlowQuestion) => {
    if (form.questions.find(q => q.id === preset.id)) return;
    setForm(f => ({ ...f, questions: [...f.questions, preset] }));
  };

  const addBlank = (type: AdminFlowQuestion['type']) => {
    setForm(f => ({ ...f, questions: [...f.questions, emptyQuestion(type, activePage)] }));
  };

  const updateQuestion = (globalIndex: number, updated: AdminFlowQuestion) => {
    setForm(f => {
      const qs = [...f.questions];
      qs[globalIndex] = updated;
      return { ...f, questions: qs };
    });
  };

  const deleteQuestion = (globalIndex: number) => {
    setForm(f => {
      const qs = [...f.questions];
      qs.splice(globalIndex, 1);
      return { ...f, questions: qs };
    });
    if (selectedIndex === globalIndex) setSelectedIndex(null);
  };

  const reorderPageQuestions = (fromLocal: number, toLocal: number) => {
    if (fromLocal === toLocal) return;
    setForm(f => {
      const qs = [...f.questions];
      const pageGlobalIndices = qs
        .map((q, i) => ({ q, i }))
        .filter(({ q }) => q.page === activePage)
        .map(({ i }) => i);
      const fromGlobal = pageGlobalIndices[fromLocal];
      const toGlobal = pageGlobalIndices[toLocal];
      const [removed] = qs.splice(fromGlobal, 1);
      qs.splice(toGlobal, 0, removed);
      return { ...f, questions: qs };
    });
    setSelectedIndex(null);
  };

  const handleCancel = () => {
    if (isDirty && !confirm('有未儲存的變更，確定要離開？')) return;
    onDone();
  };

  const handleSave = async () => {
    setError('');
    setSaving(true);
    try {
      const compressed = compressPages(form.questions.map(normalizeQuestionBeforeSave));
      // 移除 MongoDB 內部欄位，避免 _id 衝突（匯入跨環境資料時尤其重要）
      const { _id, __v, createdAt, updatedAt, ...cleanForm } = form as any;
      const payload = { ...cleanForm, questions: compressed };
      if (mode === 'create') {
        await adminApi.createFlowModel(payload);
      } else {
        await adminApi.updateFlowModel(initial.id, payload);
      }
      onDone();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900 -m-6">
      {/* MetaBar */}
      <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 py-3">
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={handleCancel} className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-100 flex items-center gap-1 shrink-0 mr-1">
            ← 返回
          </button>
          <div className="w-px h-5 bg-gray-200 dark:bg-gray-700 shrink-0" />
          {/* ID (always readonly) */}
          <div className="flex flex-col gap-0.5 w-52">
            <label className="text-[10px] text-gray-400 dark:text-gray-500 leading-none">ID</label>
            <input
              value={form.id ?? ''}
              disabled
              className="text-xs border dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 opacity-50 cursor-not-allowed font-mono"
            />
          </div>
          {/* Title */}
          <div className="flex flex-col gap-0.5 w-36">
            <label className="text-[10px] text-gray-400 dark:text-gray-500 leading-none">標題</label>
            <input value={form.title ?? ''} onChange={e => setMeta('title', e.target.value)} placeholder="標題"
              className="text-xs border dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500" />
          </div>
          {/* Desc */}
          <div className="flex flex-col gap-0.5 w-48">
            <label className="text-[10px] text-gray-400 dark:text-gray-500 leading-none">描述</label>
            <input value={form.desc ?? ''} onChange={e => setMeta('desc', e.target.value)} placeholder="描述"
              className="text-xs border dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500" />
          </div>
          {/* Category (dynamic select) */}
          <div className="flex flex-col gap-0.5 w-28">
            <label className="text-[10px] text-gray-400 dark:text-gray-500 leading-none">分類</label>
            <select value={form.category ?? ''} onChange={e => setMeta('category', e.target.value)}
              className="text-xs border dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500">
              <option value="">(未分類)</option>
              {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>
          {/* Cover image — URL or Upload */}
          <div className="flex flex-col gap-0.5 w-56">
            <div className="flex items-center gap-1.5">
              <label className="text-[10px] text-gray-400 dark:text-gray-500 leading-none">封面圖</label>
              <div className="flex text-[10px] border dark:border-gray-600 rounded overflow-hidden">
                <button type="button" onClick={() => setImageMode('url')}
                  className={`px-1.5 py-0.5 ${imageMode === 'url' ? 'bg-blue-600 text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                  URL
                </button>
                <button type="button" onClick={() => setImageMode('upload')}
                  className={`px-1.5 py-0.5 ${imageMode === 'upload' ? 'bg-blue-600 text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                  上傳
                </button>
              </div>
            </div>
            {imageMode === 'url' ? (
              <input value={form.image ?? ''} onChange={e => setMeta('image', e.target.value)} placeholder="https://..."
                className="text-xs border dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500" />
            ) : (
              <div className="flex items-center gap-1.5">
                <label className={`flex-1 text-xs border dark:border-gray-600 rounded px-2 py-1 cursor-pointer text-center ${imageUploading ? 'opacity-50' : 'hover:bg-gray-50 dark:hover:bg-gray-700'} text-gray-500 dark:text-gray-400`}>
                  {imageUploading ? '上傳中...' : '選擇圖片'}
                  <input type="file" accept="image/*" className="hidden" disabled={imageUploading}
                    onChange={async e => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setImageUploading(true);
                      try {
                        const { url } = await adminApi.uploadToolkitImage(file);
                        setMeta('image', url);
                        setImageMode('url');
                      } catch (err: any) {
                        alert(err.message || '上傳失敗');
                      } finally {
                        setImageUploading(false);
                      }
                    }}
                  />
                </label>
                {form.image && <img src={form.image} alt="" className="h-7 w-7 rounded object-cover border dark:border-gray-600" />}
              </div>
            )}
          </div>
          <button onClick={() => setShowAdvanced(v => !v)}
            className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 shrink-0 ml-auto">
            {showAdvanced ? '▲' : '▼'} 進階設定
          </button>
        </div>

        {showAdvanced && (
          <div className="mt-2 pt-2 border-t dark:border-gray-700 flex flex-col gap-3">
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">hiddenImagePrompt（隱藏圖片指令）</label>
              <textarea rows={2} value={form.hiddenImagePrompt ?? ''} onChange={e => setMeta('hiddenImagePrompt', e.target.value)}
                className={`w-full ${INP} resize-y`} />
            </div>
            {supplementTools.length > 0 && (
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5">精準補助工具（勾選後生成時自動補充 prompt）</label>
                <div className="flex flex-wrap gap-3">
                  {supplementTools.map(tool => {
                    const enabled = (form.enabledSupplementTools ?? []).includes(tool.id);
                    return (
                      <label key={tool.id} className="flex items-center gap-1.5 cursor-pointer text-xs text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={e => {
                            const current = form.enabledSupplementTools ?? [];
                            const updated = e.target.checked
                              ? [...current, tool.id]
                              : current.filter(id => id !== tool.id);
                            setForm(f => ({ ...f, enabledSupplementTools: updated }));
                          }}
                          className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                        />
                        {tool.label}
                        <span className="text-gray-400 dark:text-gray-500 font-mono text-[10px]">({tool.id})</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* PageTabs */}
      <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 flex items-center gap-1">
        {pages.map(p => (
          <button
            key={p}
            onClick={() => setActivePage(p)}
            className={`text-sm px-4 py-2.5 border-b-2 transition-colors ${
              activePage === p
                ? 'border-blue-500 text-blue-600 dark:text-blue-400 font-medium'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            第 {p} 頁
          </button>
        ))}
        <button
          onClick={() => setActivePage(maxPage + 1)}
          className="text-sm px-3 py-2.5 text-gray-400 dark:text-gray-500 hover:text-blue-500 dark:hover:text-blue-400 transition-colors border-b-2 border-transparent"
        >
          + 新增一頁
        </button>
      </div>

      {/* PagePreview */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="max-w-2xl mx-auto">
          {pageQuestions.length === 0 && (
            <div className="text-center py-12 text-gray-400 dark:text-gray-500 text-sm">
              此頁尚無題目，從下方新增
            </div>
          )}
          <div className="space-y-3">
            {pageQuestions.map(({ q, i }, localIdx) => (
              <QuestionPreviewCard
                key={i}
                q={q}
                selected={selectedIndex === i}
                isDragOver={dragQOver === localIdx && dragQFrom !== localIdx}
                onClick={() => setSelectedIndex(i)}
                onDelete={() => deleteQuestion(i)}
                onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; setDragQFrom(localIdx); }}
                onDragOver={e => { e.preventDefault(); setDragQOver(localIdx); }}
                onDrop={e => { e.preventDefault(); if (dragQFrom !== null) reorderPageQuestions(dragQFrom, localIdx); setDragQFrom(null); setDragQOver(null); }}
                onDragEnd={() => { setDragQFrom(null); setDragQOver(null); }}
              />
            ))}
          </div>

          <AddQuestionBar
            activePage={activePage}
            existingIds={existingIds}
            onAddPreset={addPreset}
            onAddBlank={addBlank}
          />
        </div>
      </div>

      {/* SaveBar */}
      <div className="bg-white dark:bg-gray-800 border-t dark:border-gray-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isDirty && <span className="text-xs text-amber-500 dark:text-amber-400">● 有未儲存的變更</span>}
          {error && <span className="text-xs text-red-500">{error}</span>}
        </div>
        <div className="flex items-center gap-3">
          <button onClick={handleCancel} className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            取消
          </button>
          <button onClick={handleSave} disabled={saving}
            className="text-sm bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium">
            {saving ? '儲存中...' : '儲存'}
          </button>
        </div>
      </div>

      {/* QuestionEditModal */}
      {selectedIndex !== null && form.questions[selectedIndex] && (
        <QuestionEditModal
          question={form.questions[selectedIndex]}
          onConfirm={updated => {
            updateQuestion(selectedIndex, updated);
            setSelectedIndex(null);
          }}
          onCancel={() => setSelectedIndex(null)}
        />
      )}
    </div>
  );
};

export default FlowModelEditor;
